import { Injectable } from "@angular/core";
import { ComponentType } from "@angular/cdk/portal";
import { MatBottomSheetConfig } from "@angular/material";
import { Observable } from "rxjs/Observable";
import { map, shareReplay, skip, take } from "rxjs/operators";
import { Router } from "@angular/router";
import { WebShopService } from "./webShop.service";
import { ValueStorageService, StorageHive, LocalKeys } from "./valueStorage.service";
import { UserHelperService } from "./userHelper.service";
import { CoreLogService } from "./log.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { BottomSheetHelperService } from "./bottomSheetHelper.service";
import { CouponScanState, RedemptionResult } from "../shared/enums/coupon.enums";
import { IReservedCoupon, WebShopMode } from "./IWebShopService";
import {
  CouponRedemptionData,
  RedemptionReservation,
} from "../components/couponRedeem/couponRedeem.facade";
import {
  ReserveCouponRedemptionRequestModel,
  ReserveCouponRedemptionResponseModel,
  ReleaseCouponRedemptionRequestModel,
  ReleaseCouponRedemptionResponseModel,
  GetRedeemedCouponsSyncModel,
  GetRedeemedCouponsResponseModel,
} from "../shared/models/coupon.model";
import { AnnounceDialogComponent, LoginDialogComponent, WarningdialogComponent } from "../modalDialogs";
import { AnounceModalResult } from "../shared/contentTypes";

const NO_RETURN_ITEM_GROUP = -1;
const UNKNOWN_SOURCE = 0;
const SINGLE_USE_CODE_MIN = 100000000;
const SINGLE_USE_CODE_MAX = 999999999;

@Injectable({ providedIn: "root" })
export class CouponRedemptionService {

  /** Cart coupon IDs, one shared stream for all consumers. */
  private _inCartCouponIds$: Observable<Set<number | string>>;

  /** Coupon IDs the server confirmed as redeemed; replaced on every load. */
  private _redeemedCouponIds: Set<number> = new Set<number>();

  /** Monotonic counter guarding ensureRedeemedCouponsLoaded against an older in-flight response overwriting a newer one. */
  private _redeemedCouponsRequestToken: number = 0;

  /** Target product of the pending coupon navigation; in memory only, so a later unrelated visit never picks it up. */
  private _pendingCouponFlowProductId: number | null = null;

  constructor(
    private _webShopService: WebShopService,
    private _valueStorageService: ValueStorageService,
    private _userHelperService: UserHelperService,
    private _coreLogService: CoreLogService,
    private _objObsInjectHelper: CircularInjectionHelper,
    private _bottomSheetHelperService: BottomSheetHelperService,
    private _router: Router,
  ) {
    const me = this;
    me._inCartCouponIds$ = me._webShopService.ShippingCartObserver.pipe(
      map(items => new Set<number | string>(
        items.filter(i => !!i.coupon_id).map(i => i.coupon_id)
      )),
      shareReplay(1)
    );
    // On every login or logout: forget the old state, and reload it after a login.
    // skip(1) ignores the first value, which is just the current state, not a change.
    me._userHelperService.RegisterObserver.pipe(skip(1)).subscribe((registered) => {
      me._redeemedCouponIds.clear();
      // A coupon started by the previous user must not carry over to the next one.
      me._pendingCouponFlowProductId = null;
      if (registered) {
        me.ensureRedeemedCouponsLoaded();
      }
    });
    // At app start: load once if the user is already logged in.
    me._userHelperService.RegisterObserver.pipe(take(1)).subscribe((registered) => {
      if (registered) {
        me.ensureRedeemedCouponsLoaded();
      }
    });
    // When the guest removes a coupon from the cart, free its reservation.
    me._webShopService.couponRemovedFromCart$.subscribe((redemptionId) => {
      me.releaseRedemption(redemptionId);
    });
  }

  //#region Cart State

  /** Cart coupon IDs as a live stream. */
  public get inCartCouponIds$(): Observable<Set<number | string>> {
    const me = this;
    return me._inCartCouponIds$;
  }

  /** True when the coupon is already in the cart. */
  public isInCart(couponId: number | string): boolean {
    const me = this;
    return me._webShopService.getShippingCartItems().some(i => i.coupon_id === couponId);
  }

  /** True when the coupon should be shown dimmed: redeemed or already in the cart. */
  public isCouponDisabled(couponId: number | string, isRedeemed: boolean): boolean {
    const me = this;
    return isRedeemed || me.isInCart(couponId);
  }

  //#endregion Cart State

  //#region Redemption Flow

  /** Redeems a coupon from the coupon list or detail. */
  public async resolveAppCouponRedemption(coupon: CouponRedemptionData): Promise<RedemptionResult> {
    const me = this;
    if (coupon.isRedeemed) {
      return RedemptionResult.AlreadyRedeemed;
    }
    const isAlreadyInCart = me.isInCart(coupon.id);
    if (isAlreadyInCart) {
      return RedemptionResult.AlreadyInCart;
    }
    // Single-use coupons always need a login, even if the coupon is marked as anonymous.
    const isLoginRequired = coupon.isRedeemOncePerUser
      ? !me._userHelperService.IsRegistered
      : (!coupon.anonymCode && !me._userHelperService.IsRegistered);
    if (isLoginRequired) {
      return RedemptionResult.LoginRequired;
    }
    if (coupon.isRedeemOncePerUser && !me.supportsSingleUseCoupon()) {
      return RedemptionResult.UnsupportedOrderMode;
    }
    if (!coupon.isRedeemOncePerUser) {
      const scanState = await me._webShopService.scanCouponCode(
        coupon.couponCode, true /* isAppCoupon */, { backItemGroupId: NO_RETURN_ITEM_GROUP }
      );
      return me.mapScanStateToResult(scanState);
    }
    return me.redeemSingleUseCoupon(coupon);
  }

  /** Reserves a single-use coupon and opens its free product. */
  private async redeemSingleUseCoupon(coupon: CouponRedemptionData): Promise<RedemptionResult> {
    const me = this;
    const orderContext = {
      hypCusNr: me._webShopService.locationID,
      orderType: me._webShopService.mapWebShopModeToOrderType(me._webShopService.getServiceMode()),
    };
    const reservation = await me.reserveRedemption(coupon.id, orderContext);
    if (reservation.result !== RedemptionResult.Proceed) {
      return reservation.result;
    }
    if (!reservation.productId) {
      // Without a product from the server the coupon can't be redeemed.
      me.releaseRedemption(reservation.redemptionId);
      return RedemptionResult.NotAvailable;
    }
    let state: CouponScanState;
    try {
      state = await me._webShopService.findAndNavigateToProductByProductId(
        reservation.productId,
        { backItemGroupId: NO_RETURN_ITEM_GROUP, onBeforeOpenProduct: (productId) => me.markCouponFlowNavigation(productId) }
      );
    } catch (error) {
      // Opening the product failed: undo the coupon marker and free the reservation,
      // otherwise the coupon stays blocked for 1 h.
      me.unmarkCouponProduct(reservation.productId);
      me.releaseRedemption(reservation.redemptionId);
      throw error;
    }
    if (state === CouponScanState.OK) {
      await me.saveReservedCoupon(coupon.id, reservation.code, reservation.redemptionId, reservation.productId);
    } else {
      me.releaseRedemption(reservation.redemptionId);
    }
    return me.mapScanStateToResult(state);
  }

  /** Redeems a typed or scanned coupon code. */
  public async resolveCouponCodeRedemption(couponCode: string): Promise<RedemptionResult> {
    const me = this;
    if (!couponCode) {
      return RedemptionResult.WrongCode;
    }
    const numericValue = Number(couponCode);
    // Single-use codes can't be redeemed by typing or scanning.
    if (me.isSingleUseCode(numericValue)) {
      return RedemptionResult.InvalidCode;
    }
    const scanState = await me._webShopService.scanCouponCode(
      couponCode, false /* isAppCoupon */, { backItemGroupId: NO_RETURN_ITEM_GROUP }
    );
    return me.mapScanStateToResult(scanState);
  }

  /** True when the current order mode allows single-use coupons. */
  public supportsSingleUseCoupon(): boolean {
    const me = this;
    const mode = me._webShopService.getServiceMode();
    return mode === WebShopMode.Retail
      || mode === WebShopMode.TakeAway
      || mode === WebShopMode.Delivery;
  }

  private isSingleUseCode(value: number): boolean {
    return value >= SINGLE_USE_CODE_MIN && value <= SINGLE_USE_CODE_MAX;
  }

  /** Maps the scan state to a RedemptionResult. */
  private mapScanStateToResult(state: CouponScanState): RedemptionResult {
    const me = this;
    if (state === CouponScanState.OK) { return RedemptionResult.Proceed; }
    if (state === CouponScanState.WrongCode) { return RedemptionResult.WrongCode; }
    if (state === CouponScanState.NotAvailable) { return RedemptionResult.NotAvailable; }
    me._coreLogService.error("CouponRedemptionService.mapScanStateToResult", "unknown scan state", state);
    return RedemptionResult.WrongCode;
  }

  /**
   * Reserves the coupon on the server; never rejects, every error becomes WrongCode.
   * A repeat reserve within the 1 h validity window returns the same code, so a repeated click is safe.
   */
  public async reserveRedemption(
    couponId: number | string,
    orderContext?: { hypCusNr: number; orderType: number }
  ): Promise<RedemptionReservation> {
    const me = this;
    const requestModel: ReserveCouponRedemptionRequestModel = {
      coupon_id: Number(couponId),
      source: UNKNOWN_SOURCE, // TODO DEV-131: the server sets source on Confirm
    };
    if (orderContext !== undefined) {
      requestModel.hyp_cus_nr = orderContext.hypCusNr;
      requestModel.order_type = orderContext.orderType;
    }
    try {
      const response = await me._objObsInjectHelper.yourAppServer.Api.RequestService<ReserveCouponRedemptionResponseModel>(
        ReserveCouponRedemptionResponseModel, "coupon/ReserveRedemption", requestModel, null, true
      );
      if (response.Data && response.Data.success) {
        return { result: RedemptionResult.Proceed, redemptionId: response.Data.redemption_id, code: response.Data.code, productId: response.Data.product_id };
      }
      // Every Reserve error shows the same "invalid" dialog.
      me._coreLogService.info("CouponRedemptionService.reserveRedemption", "ReserveRedemption rejected", response.Data ? response.Data.error : "no data");
      return { result: RedemptionResult.WrongCode };
    } catch (error) {
      me._coreLogService.error("CouponRedemptionService.reserveRedemption", "ReserveRedemption failed", me.sanitizeError(error));
      return { result: RedemptionResult.WrongCode };
    }
  }

  /** Releases the reservation in the background; errors are only logged. */
  public async releaseRedemption(redemptionId: number): Promise<void> {
    const me = this;
    const requestModel: ReleaseCouponRedemptionRequestModel = { redemption_id: redemptionId };
    try {
      const response = await me._objObsInjectHelper.yourAppServer.Api.RequestService<ReleaseCouponRedemptionResponseModel>(
        ReleaseCouponRedemptionResponseModel, "coupon/ReleaseRedemption", requestModel, null, true
      );
      if (!response.Data || !response.Data.success) {
        me._coreLogService.error("CouponRedemptionService.releaseRedemption", "ReleaseRedemption rejected", response.Data ? response.Data.error : "no data");
      }
    } catch (error) {
      me._coreLogService.error("CouponRedemptionService.releaseRedemption", "ReleaseRedemption failed", me.sanitizeError(error));
    }
  }

  /** Loads the redeemed coupons from the server; a stale in-flight response can never overwrite a result from a later call. */
  public async ensureRedeemedCouponsLoaded(): Promise<void> {
    const me = this;
    const requestToken = ++me._redeemedCouponsRequestToken;
    try {
      const response = await me._objObsInjectHelper.yourAppServer.Api.RequestService<GetRedeemedCouponsResponseModel>(
        GetRedeemedCouponsResponseModel, "coupon/GetRedeemedCoupons", new GetRedeemedCouponsSyncModel()
      );
      if (requestToken !== me._redeemedCouponsRequestToken) {
        return;
      }
      if (response.Data) {
        me._redeemedCouponIds = new Set<number>(response.Data.redeemed_coupons.map(redeemed => redeemed.coupon_id));
      }
    } catch (error) {
      me._coreLogService.error("CouponRedemptionService.ensureRedeemedCouponsLoaded", "GetRedeemedCoupons failed", me.sanitizeError(error));
    }
  }

  /** Reduces an error to a loggable, PII-safe summary. */
  private sanitizeError(error: any): string {
    if (!error) { return "unknown error"; }
    return error.codeText || error.code || error.message || String(error);
  }

  /** True when the coupon is in the last loaded redeemed set. */
  public isCouponRedeemed(couponId: number | string): boolean {
    const me = this;
    return me._redeemedCouponIds.has(Number(couponId));
  }

  //#endregion Redemption Flow

  //#region Reserved Coupon

  /** Marks productId as the pending coupon-flow target; also used to re-arm the marker for a chosen recipe-linked variant. */
  public markCouponFlowNavigation(productId: number): void {
    const me = this;
    me._pendingCouponFlowProductId = productId;
  }

  /** True if this product was opened from the coupon flow; resets the marker. */
  public consumeCouponFlowNavigationMarker(productId: number): boolean {
    const me = this;
    const matches = me._pendingCouponFlowProductId === productId;
    me._pendingCouponFlowProductId = null;
    return matches;
  }

  /** Undoes a markCouponFlowNavigation call, but only if the marker still points at productId — a newer re-arm for a different product is left untouched. */
  public unmarkCouponProduct(productId: number): void {
    const me = this;
    if (me._pendingCouponFlowProductId === productId) {
      me._pendingCouponFlowProductId = null;
    }
  }

  /** Stores the coupon context until add-to-cart; survives a reload, cleared on logout. */
  public async saveReservedCoupon(couponId: number | string, couponCode: number | string, redemptionId: number, productId?: number): Promise<void> {
    const me = this;
    const reservedCoupon: IReservedCoupon = { couponId, couponCode, redemptionId, productId };
    await me._valueStorageService.set(StorageHive.Local, LocalKeys.reservedCoupon, reservedCoupon);
  }

  /** Reads the reserved coupon without clearing it; returns null if it is incomplete. */
  public async getReservedCoupon(): Promise<IReservedCoupon | null> {
    const me = this;
    const reservedCoupon = await me._valueStorageService.get(StorageHive.Local, LocalKeys.reservedCoupon) as IReservedCoupon;
    const isComplete = reservedCoupon != null && reservedCoupon.couponId != null && reservedCoupon.couponCode != null && reservedCoupon.redemptionId != null;
    return isComplete ? reservedCoupon : null;
  }

  /**
   * Discards the reserved coupon, e.g. when the product is left without add-to-cart.
   * Does not release the server-side reservation — it is left to expire on its own within the validity window.
   */
  public async discardReservedCoupon(): Promise<void> {
    const me = this;
    await me._valueStorageService.remove(StorageHive.Local, LocalKeys.reservedCoupon);
  }

  /** Marks a basket coupon-line re-open; persists across a reload so the lock on menuDetail survives a refresh. */
  public async markBasketCouponRowNavigation(productId: number): Promise<void> {
    const me = this;
    await me._valueStorageService.set(StorageHive.Local, LocalKeys.basketCouponRowProductId, productId);
  }

  /** Reads the basket-coupon-row marker without clearing it, so a reload on the same page keeps the lock. */
  public async getBasketCouponRowProductId(): Promise<number | null> {
    const me = this;
    const productId = await me._valueStorageService.get(StorageHive.Local, LocalKeys.basketCouponRowProductId);
    return productId != null ? productId : null;
  }

  /** Clears the basket-coupon-row marker when the detail page is left, so a later unrelated visit never inherits it. */
  public async clearBasketCouponRowMarker(): Promise<void> {
    const me = this;
    await me._valueStorageService.remove(StorageHive.Local, LocalKeys.basketCouponRowProductId);
  }

  //#endregion Reserved Coupon

  //#region Dialogs

  /** Shows the dialog for a result; LoginRequired and UnsupportedOrderMode are handled by the caller. */
  public routeRedemptionResult(result: RedemptionResult, afterDialog: () => void): void {
    const me = this;
    switch (result) {
      case RedemptionResult.Proceed:
        afterDialog();
        break;
      case RedemptionResult.AlreadyRedeemed:
        me.openAlreadyRedeemedDialog(afterDialog);
        break;
      case RedemptionResult.AlreadyInCart:
        me.openInCartDialog(afterDialog);
        break;
      case RedemptionResult.WrongCode:
        me.openWrongCodeDialog(afterDialog);
        break;
      case RedemptionResult.NotAvailable:
        me.openNotAvailableDialog(afterDialog);
        break;
      case RedemptionResult.InvalidCode:
        me.openWrongCodeDialog(afterDialog);
        break;
      default:
        me._coreLogService.error("CouponRedemptionService.routeRedemptionResult", "unexpected result reached router", result);
        afterDialog();
        break;
    }
  }

  /** Dialog: coupon already redeemed. */
  public openAlreadyRedeemedDialog(afterClose?: () => void): void {
    const me = this;
    me.openWarningDialog("phCoupon_Redeem_Error_AlreadyRedeemed_Title", "phCoupon_Redeem_Error_AlreadyRedeemed", afterClose);
  }

  /** Dialog: coupon invalid. */
  public openWrongCodeDialog(afterClose?: () => void): void {
    const me = this;
    me.openWarningDialog("phCoupon_Redeem_Code_Not_Valid_Title", "phCoupon_Redeem_Code_Not_Valid_SubText", afterClose);
  }

  /** Dialog: product not available. */
  public openNotAvailableDialog(afterClose?: () => void): void {
    const me = this;
    me.openWarningDialog("phCoupon_Redeem_Product_Not_Available_Title", "phCoupon_Redeem_Product_Not_Available_SubText", afterClose);
  }

  /** Dialog: coupon already in the cart; first button opens the cart. */
  public openInCartDialog(afterClose?: () => void): void {
    const me = this;
    me.openAnnounceDialog(
      {
        title: "phCoupon_InCart_Dialog_Title",
        content: "phCoupon_InCart_Dialog_Text",
        firstBtnLabel: "phCoupon_InCart_Dialog_ToCart",
        secondBtnLabel: "phCoupon_InCart_Dialog_Back",
      },
      () => me._router.navigate(["/webshop"]),
      true,
      afterClose
    );
  }

  /** Dialog: order mode not supported; first button opens the order mode selection (no afterClose). */
  public openUnsupportedOrderModeDialog(afterClose?: () => void): void {
    const me = this;
    me.openAnnounceDialog(
      {
        title: "phCoupon_Redeem_OrderType_Not_Valid_Title",
        content: "phCoupon_Redeem_OrderType_Not_Valid_SubText",
        firstBtnLabel: "phCoupon_Redeem_OrderType_Not_Valid_Change_Btn",
        secondBtnLabel: "phCoupon_Redeem_OrderType_Not_Valid_Cancel_Btn",
      },
      () => me._router.navigate(["/menuservice"]),
      false,
      afterClose
    );
  }

  /** Opens the login sheet. */
  public openLoginDialog(afterClose?: () => void): void {
    const me = this;
    me.openDismissableSheet(LoginDialogComponent, undefined, afterClose);
  }

  /** Warning sheet with title, text and a Return button. */
  private openWarningDialog(title: string, content: string, afterClose?: () => void): void {
    const me = this;
    me.openDismissableSheet(WarningdialogComponent, {
      data: { title, content, btnLabel: "phGlobal_Return" },
      panelClass: "mat-bottom-sheet-container-medium",
    }, afterClose);
  }

  /** Two-button sheet; the first button runs onFirstButton. */
  private openAnnounceDialog(
    data: { title: string; content: string; firstBtnLabel: string; secondBtnLabel: string },
    onFirstButton: () => void,
    afterCloseOnFirstButton: boolean,
    afterClose?: () => void
  ): void {
    const me = this;
    const dialogRef = me._bottomSheetHelperService.open(AnnounceDialogComponent, {
      data,
      panelClass: "mat-bottom-sheet-container-medium",
    });
    dialogRef.afterDismissed().subscribe((result: AnounceModalResult) => {
      if (result === AnounceModalResult.FirstButton) {
        onFirstButton();
        if (!afterCloseOnFirstButton) {
          return;
        }
      }
      if (afterClose) { afterClose(); }
    });
  }

  /** Opens a sheet and calls afterClose when it is dismissed. */
  private openDismissableSheet(component: ComponentType<any>, config?: MatBottomSheetConfig, afterClose?: () => void): void {
    const me = this;
    const sheet = me._bottomSheetHelperService.open(component, config);
    sheet.afterDismissed().subscribe(() => {
      if (afterClose) { afterClose(); }
    });
  }

  //#endregion Dialogs

}
